import {
    Component,
    ViewChild,
    AfterViewInit,
    ChangeDetectorRef,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { exportDataGrid } from 'devextreme/excel_exporter';
import {
    MessageType,
    NotificationService,
    UtilService,
    UnifiedQueryV2Service,
} from 'src/app/shared/services';
import { Workbook } from 'exceljs';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import {
    concat as _concat,
    each as _each,
    filter as _filter,
    get as _get,
    includes as _includes,
    pull as _pull,
} from 'lodash';
import {
    DxDataGridComponent,
    DxDataGridModule,
    DxToolbarModule,
} from 'devextreme-angular';
import { FormatNumberPipeModule } from 'src/app/shared/pipes';
import { forkJoin, Observable } from 'rxjs';
import { ItemMarkService } from './services/item-mark.service';

@Component({
    selector: 'app-price-list',
    templateUrl: './inventory-list.component.html',
    styleUrls: ['./inventory-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxToolbarModule, DxDataGridModule, FormatNumberPipeModule],
})
export class InventoryListComponent implements AfterViewInit {
    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;

    pricelistSource: any[] = [];
    isDataLoading = false;
    isHideDiscontinued = true;
    discontinuedItemNum = 0;
    markedCount = 0;
    markedPct = 0;

    hideDiscontinuedProductCheckBoxOptions = {
        text: 'Hide Discontinued',
        value: this.isHideDiscontinued,
        onValueChanged: (e: { value: boolean }) => {
            this.isHideDiscontinued = e.value;
            this.fetchInventory();
        },
    };
    refreshInventoryButtonOptions = {
        icon: 'refresh',
        stylingMode: 'outlined',
        onClick: this.fetchInventory.bind(this),
    };
    exportExcelButtonOptions = {
        text: 'Export Excel',
        icon: 'exportxlsx',
        stylingMode: 'outlined',
        onClick: this.exportExcel.bind(this),
    };

    private cdref = inject(ChangeDetectorRef);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private itemMarkService = inject(ItemMarkService);

    ngAfterViewInit(): void {
        this.fetchInventory();
    }

    getItemLastUpdated(data: {
        crawler_last_updated: moment.MomentInput;
    }): string {
        return moment(data.crawler_last_updated).fromNow();
    }

    private fetchInventory(): void {
        if (!this.isDataLoading) {
            this.isDataLoading = true;
            this.cdref.detectChanges();
            this.dataGrid.instance.beginCustomLoading('Loading ...');
            this.pricelistSource.length = 0;
            this.discontinuedItemNum = 0;
            this.markedCount = 0;
            this.markedPct = 0;

            const obsList: Observable<any>[] = [
                this.unifiedQueryV2Service.query({
                    db: 'lnw_crawler',
                    col: 'inventory',
                    query: {
                        is_show: true,
                    },
                    options: {
                        projection: {
                            sku: 1,
                            title: 1,
                            price: 1,
                            purl: 1,
                            stock_status: 1,
                            is_show: 1,
                            remain: 1,
                            sell: 1,
                            crawler_last_updated: 1,
                        },
                    },
                }),
                this.itemMarkService.getMarked(),
            ];

            forkJoin(obsList)
                .subscribe(
                    (results: any[]) => {
                        const invenResult = results[0];
                        let inventoryRecords = this.decorateInventoryRecord(
                            invenResult,
                            results[1],
                        );
                        if (this.isHideDiscontinued) {
                            inventoryRecords = _filter(
                                inventoryRecords,
                                (result) => {
                                    return !result.is_discontinued;
                                },
                            );
                        }
                        this.pricelistSource = _concat(
                            this.pricelistSource,
                            inventoryRecords,
                        );
                        this.calculateMarkCount();
                    },
                    (err) => {
                        console.error(err);
                        this.notificationService.pushMessage({
                            displayTime: 6000,
                            message: this.utilService.errorToString(err),
                            messageType: MessageType.error,
                        });
                    },
                )
                .add(() => {
                    this.dataGrid.instance.endCustomLoading();
                    this.isDataLoading = false;
                    this.cdref.detectChanges();
                });
        }
    }

    private decorateInventoryRecord(records: any[], markResult: number[]) {
        _each(records, (record) => {
            if (record.stock_status === 'discontinued') {
                record.is_discontinued = true;
                this.discontinuedItemNum++;
            } else {
                record.is_discontinued = false;
            }

            const isMarked = _includes(markResult, record['_id']);
            record['isMarked'] = isMarked;
            if (isMarked) {
                _pull(markResult, record['_id']);
            }

            record['pid'] = record['_id'];
            record['price'] = _get(record, 'price.$numberDecimal');
        });
        return records;
    }

    onCellDblClick(e: any) {
        window.getSelection()?.removeAllRanges();
        if (e.column.dataField === 'isMarked') {
            const pid = e.key;
            if (pid) {
                this.dataGrid.instance.beginCustomLoading('Marking ...');
                const currentMark = e?.data?.isMarked ? true : false;
                this.itemMarkService.mark([pid], !currentMark).subscribe({
                    next: (res) => {
                        this.dataGrid.instance.endCustomLoading();
                        if (res) {
                            e.data.isMarked = !currentMark;
                            this.calculateMarkCount();
                            this.dataGrid.instance.refresh();
                        } else {
                            this.notificationService.pushMessage({
                                displayTime: 6000,
                                message:
                                    this.utilService.errorToString(
                                        'Unexpected error',
                                    ),
                                messageType: MessageType.error,
                            });
                        }
                    },
                    error: (err) => {
                        this.dataGrid.instance.endCustomLoading();
                        console.error('Mark failed: ', err);
                        this.notificationService.pushMessage({
                            displayTime: 6000,
                            message: this.utilService.errorToString(err),
                            messageType: MessageType.error,
                        });
                    },
                });
            }
        }
    }

    private calculateMarkCount(): void {
        let markedCount = 0;
        _each(this.pricelistSource, (item) => {
            if (item['isMarked']) {
                markedCount++;
            }
        });
        this.markedCount = markedCount;
        const totalItems = this.pricelistSource.length;
        this.markedPct =
            totalItems > 0 ? (this.markedCount / totalItems) * 100 : 0;
    }

    private exportExcel(): void {
        const workbook = new Workbook();
        const sheet = workbook.addWorksheet('Inventory');
        exportDataGrid({
            component: this.dataGrid.instance,
            worksheet: sheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
                FileSaver.saveAs(
                    new Blob([buffer], { type: 'application/octet-stream' }),
                    `Inventory ${moment().format(
                        'YYYY_MM_DD-HH_mm_ss_SS',
                    )}.xlsx`,
                );
            });
        });
    }
}
